import React, { useRef, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Chip, ButtonBase } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import MenuIcon from '@mui/icons-material/Menu';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import GroupIcon from '@mui/icons-material/Group';
import DescriptionIcon from '@mui/icons-material/Description';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VideocamIcon from '@mui/icons-material/Videocam';
import UpdateIcon from '@mui/icons-material/Update';
import ScheduleIcon from '@mui/icons-material/Schedule';
import RateReviewIcon from '@mui/icons-material/RateReview';
import EmailIcon from '@mui/icons-material/Email';
import SummarizeIcon from '@mui/icons-material/Summarize';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ChatInput from './ChatInput';
import DocumentPanel from './DocumentPanel';
import ChatPanel from './ChatPanel';

const PROJECT_SUGGESTIONS = [
  {
    title: 'Pre-productie',
    items: [
      { icon: <DescriptionIcon sx={{ fontSize: 18 }} />, label: 'Schrijf een briefing' },
      { icon: <RequestQuoteIcon sx={{ fontSize: 18 }} />, label: 'Help met de offerte' },
      { icon: <CalendarTodayIcon sx={{ fontSize: 18 }} />, label: 'Maak een planning' },
    ],
  },
  {
    title: 'Productie',
    items: [
      { icon: <VideocamIcon sx={{ fontSize: 18 }} />, label: 'Wat zijn de draaidagen?' },
      { icon: <UpdateIcon sx={{ fontSize: 18 }} />, label: 'Maak een statusupdate' },
      { icon: <ScheduleIcon sx={{ fontSize: 18 }} />, label: 'Wat zijn de deadlines?' },
    ],
  },
  {
    title: 'Post-productie',
    items: [
      { icon: <RateReviewIcon sx={{ fontSize: 18 }} />, label: 'Bereid feedback voor' },
      { icon: <EmailIcon sx={{ fontSize: 18 }} />, label: 'Schrijf oplevermail' },
      { icon: <SummarizeIcon sx={{ fontSize: 18 }} />, label: 'Maak een samenvatting' },
    ],
  },
];

const GENERIC_SUGGESTIONS = [
  {
    title: 'Algemeen',
    items: [
      { icon: <EditNoteIcon sx={{ fontSize: 18 }} />, label: 'Help me een tekst schrijven' },
      { icon: <LightbulbIcon sx={{ fontSize: 18 }} />, label: 'Start een brainstorm' },
      { icon: <EmailIcon sx={{ fontSize: 18 }} />, label: 'Schrijf een e-mail' },
      { icon: <SummarizeIcon sx={{ fontSize: 18 }} />, label: 'Vat een document samen' },
    ],
  },
];

export default function ChatArea({
  messages,
  onSend,
  isStreaming,
  onStop,
  model,
  onModelChange,
  models,
  onOpenPrompts,
  onOpenAgents,
  agentName,
  agentIcon,
  onToggleSidebar,
  isMobile,
  agents = [],
  clients = [],
  selectedClientId,
  onClientChange,
  agentToInsert,
  onAgentInserted,
  participants = [],
  hasProjectId,
  onToggleProjectOverview,
  showProjectOverview,
  folderName,
  projectStatus,
}) {
  const scrollRef = useRef(null);
  const isEmpty = messages.length === 0;

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const suggestions = hasProjectId ? PROJECT_SUGGESTIONS : GENERIC_SUGGESTIONS;

  // For the workspace layout: extract last assistant message as document content
  const lastAssistantMsg = !isEmpty ? [...messages].reverse().find(m => m.role === 'assistant') : null;
  const documentContent = lastAssistantMsg?.content || '';
  const documentIsStreaming = lastAssistantMsg?.isStreaming || false;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minWidth: 0 }}>
      {/* Top bar with sidebar toggle */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        px: 2,
        py: 0.5,
        borderBottom: '1px solid rgba(45,51,53,0.06)',
        bgcolor: '#fff',
        minHeight: 40,
      }}>
        <Tooltip title="Sidebar">
          <IconButton size="small" onClick={onToggleSidebar} sx={{ mr: 0.5 }}>
            <MenuIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {agentName && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <SmartToyIcon sx={{ fontSize: 16, color: '#526075' }} />
            <Typography fontSize={13} fontWeight={600} color="#526075">{agentName}</Typography>
          </Box>
        )}
        {participants.length > 0 && (
          <Tooltip
            title={participants.map((p) => p.user_name || p.user_email).join(', ')}
            arrow
          >
            <Chip
              icon={<GroupIcon sx={{ fontSize: 14 }} />}
              label={`Gedeeld met ${participants.length} ${participants.length === 1 ? 'persoon' : 'personen'}`}
              size="small"
              sx={{
                ml: 1,
                height: 24,
                fontSize: 11,
                bgcolor: '#dbcdff',
                color: '#635983',
                '& .MuiChip-icon': { color: '#635983' },
              }}
            />
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }} />
        {hasProjectId && (
          <Tooltip title={showProjectOverview ? 'Projectoverzicht sluiten' : 'Projectoverzicht'}>
            <IconButton
              size="small"
              onClick={onToggleProjectOverview}
              sx={{ color: showProjectOverview ? '#526075' : '#adb3b5' }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {isEmpty ? (
        /* ─── Centered welcome screen ─── */
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          overflowY: 'auto',
          px: { xs: 1.5, sm: 3 },
          py: 4,
          bgcolor: '#f8f9fa',
        }}>
          <Box sx={{ width: '100%', maxWidth: 640 }}>
            {/* Title */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2d3335', mb: 0.5 }}>
                Full Frame AI Chat
              </Typography>
              {folderName && (
                <Typography sx={{ fontSize: 14, color: '#adb3b5' }}>
                  {folderName}
                </Typography>
              )}
            </Box>

            {/* Chat input */}
            <Box sx={{ mb: 3 }}>
              <ChatInput
                onSend={onSend}
                isStreaming={isStreaming}
                onStop={onStop}
                model={model}
                onModelChange={onModelChange}
                models={models}
                onOpenPrompts={onOpenPrompts}
                onOpenAgents={onOpenAgents}
                agents={agents}
                clients={clients}
                selectedClientId={selectedClientId}
                onClientChange={onClientChange}
                agentToInsert={agentToInsert}
                onAgentInserted={onAgentInserted}
              />
            </Box>

            {/* Suggestions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {suggestions.map((group) => (
                <Box key={group.title}>
                  <Typography sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: '#adb3b5',
                    mb: 0.75,
                    px: 0.5,
                  }}>
                    {group.title}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {group.items.map((item) => (
                      <ButtonBase
                        key={item.label}
                        onClick={() => onSend(item.label)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.25,
                          borderRadius: '12px',
                          border: '1px solid rgba(45,51,53,0.06)',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          mb: 0.75,
                          transition: 'all 0.15s',
                          '&:hover': {
                            bgcolor: '#f1f4f5',
                            borderColor: 'rgba(45,51,53,0.12)',
                          },
                        }}
                      >
                        <Box sx={{ color: '#767c7e', display: 'flex', alignItems: 'center' }}>
                          {item.icon}
                        </Box>
                        <Typography sx={{ fontSize: 14, color: '#556071' }}>
                          {item.label}
                        </Typography>
                      </ButtonBase>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      ) : (
        /* ─── 2-column workspace layout ─── */
        <Box sx={{
          display: 'flex',
          flex: 1,
          overflow: 'hidden',
          flexDirection: { xs: 'column', md: 'row' },
        }}>
          <DocumentPanel
            content={documentContent}
            isStreaming={documentIsStreaming}
            title={folderName || 'Document'}
            agentName={agentName}
            projectStatus={projectStatus}
          />
          <ChatPanel
            messages={messages}
            onSend={onSend}
            isStreaming={isStreaming}
            onStop={onStop}
            model={model}
            onModelChange={onModelChange}
            models={models}
            onOpenPrompts={onOpenPrompts}
            onOpenAgents={onOpenAgents}
            agentName={agentName}
            agentIcon={agentIcon}
            agents={agents}
            clients={clients}
            selectedClientId={selectedClientId}
            onClientChange={onClientChange}
            agentToInsert={agentToInsert}
            onAgentInserted={onAgentInserted}
          />
        </Box>
      )}
    </Box>
  );
}
